import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';


const VideoPlayer = ({ videoUrl, title, duration }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [quality, setQuality] = useState('720p');
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const videoRef = useRef(null);

  const qualities = ['1080p', '720p', '480p', '360p'];

  const togglePlay = () => {
    if (videoRef?.current) {
      if (isPlaying) {
        videoRef?.current?.pause();
      } else {
        videoRef?.current?.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef?.current) {
      setCurrentTime(videoRef?.current?.currentTime);
    }
  };

  const handleSeek = (e) => {
    const seekTime = (e?.target?.value / 100) * duration;
    if (videoRef?.current) {
      videoRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };

  const toggleMute = () => {
    if (videoRef?.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = e?.target?.value / 100;
    setVolume(newVolume);
    if (videoRef?.current) {
      videoRef.current.volume = newVolume;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="w-full bg-card rounded-lg overflow-hidden shadow-md">
      <div className="relative aspect-video bg-muted">
        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full object-cover"
          onTimeUpdate={handleTimeUpdate}
          onEnded={() => setIsPlaying(false)}
        />
        
        {!isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <button
              onClick={togglePlay}
              className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 bg-primary hover:bg-primary/90 rounded-full flex items-center justify-center transition-smooth hover-lift active-press"
              aria-label="Play video"
            >
              <Icon name="Play" size={32} color="var(--color-primary-foreground)" />
            </button>
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 md:p-4">
          <div className="flex items-center gap-2 md:gap-3 mb-2">
            <button
              onClick={togglePlay}
              className="p-1.5 md:p-2 hover:bg-white/20 rounded-lg transition-smooth"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              <Icon 
                name={isPlaying ? 'Pause' : 'Play'} 
                size={20} 
                color="white" 
              />
            </button>

            <button
              onClick={toggleMute}
              className="p-1.5 md:p-2 hover:bg-white/20 rounded-lg transition-smooth"
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              <Icon 
                name={isMuted ? 'VolumeX' : 'Volume2'} 
                size={20} 
                color="white" 
              />
            </button>

            <input
              type="range"
              min="0"
              max="100"
              value={volume * 100}
              onChange={handleVolumeChange}
              className="w-16 md:w-20 h-1 bg-white/30 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full"
              aria-label="Volume control"
            />

            <span className="text-white text-xs md:text-sm data-text ml-auto">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>

            <div className="relative">
              <button
                onClick={() => setShowQualityMenu(!showQualityMenu)}
                className="p-1.5 md:p-2 hover:bg-white/20 rounded-lg transition-smooth"
                aria-label="Quality settings"
              >
                <Icon name="Settings" size={20} color="white" />
              </button>

              {showQualityMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowQualityMenu(false)}
                  />
                  <div className="absolute bottom-full right-0 mb-2 bg-popover border border-border rounded-lg shadow-xl z-20 overflow-hidden min-w-[120px]">
                    {qualities?.map((q) => (
                      <button
                        key={q}
                        onClick={() => {
                          setQuality(q);
                          setShowQualityMenu(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-muted transition-smooth ${
                          quality === q ? 'bg-primary text-primary-foreground' : 'text-foreground'
                        }`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <input
            type="range"
            min="0"
            max="100"
            value={progress}
            onChange={handleSeek}
            className="w-full h-1 bg-white/30 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:rounded-full"
            aria-label="Video progress"
          />
        </div>
      </div>
      <div className="p-4 md:p-5 lg:p-6">
        <h2 className="text-lg md:text-xl lg:text-2xl font-heading font-semibold text-foreground">
          {title}
        </h2>
      </div>
    </div>
  );
};

export default VideoPlayer;